import { createContext, useContext } from 'react';
import type { Context } from 'react';

interface RowRegistry<T> {
  Context: Context<T | null>;
  useRegistry: () => T;
}

/**
 * Shared scaffolding for the row-scoped registries (ReceivableRegistry,
 * InvoiceDraftRegistry): a typed context plus a guarded accessor hook that
 * throws when used outside its provider. Each registry keeps its own provider
 * and value body; only this createContext/useContext plumbing is shared, so a
 * draft edit in one registry never touches the other's context.
 */
export const createRowRegistry = <T>(
  hookName: string,
  providerName: string,
): RowRegistry<T> => {
  const RegistryContext = createContext<T | null>(null);

  const useRegistry = (): T => {
    const value = useContext(RegistryContext);
    if (!value) {
      throw new Error(`${hookName} must be used within a ${providerName}`);
    }
    return value;
  };

  return { Context: RegistryContext, useRegistry };
};
